import { useEffect, useState } from 'react'
import type { userInformationInterface } from '@/src/common/interfaces/user'
import { useLazyQuery, useMutation } from '@apollo/client'
import { LOGIN_USER, VERIFY_OTP } from '@/src/common/gql/mutations/user'
import { useTokenStore } from '@/src/common/store/useTokenStore'
import { ScrollView, View, Keyboard, Platform, Text, StyleSheet } from 'react-native'
import { Routes } from '@/src/common/enums/routes'
import { InputCode } from '@/src/common/components/Input/InputCode'
import { BackButton } from '@/src/common/components/BackButton'
import { OnboardingWrapper } from '@/src/common/wrappers/OnboardingWrapper'
import { useLogError } from '@/src/common/hooks/useLogError'
import { Button } from '@/src/common/components/Button'
import { RESEND_OTP_TIMER } from '@/src/common/utils/constants'
import { triggerSegmentAction, triggerSegmentIdentify } from '@/src/services/segment'
import { GET_USER_INFO } from '@/src/common/gql/queries/user'
import type { GetUserInfoQuery } from '@api/mesa/graphql'
import { AuthenticationE2E } from '@/src/experiences/authentication/e2e'
import { useSardine } from '@/src/services/Sardine'
import { sardineIdKey } from '@/src/services/Sardine/SardineId'
import { useUserStore } from '@/src/common/store/useUserStore'
import { withRecaptchaVerification } from '@/src/common/utils/withRecaptchaVerification'
import { useFeatureGate } from '@/src/services/Statsig/useFeatureGate'
import { FeatureGates } from '@/src/common/enums/FeatureGates'
import { useEvent } from 'tamagui'
import { typography } from '@/src/common/theme'

const OTP_LENGTH = 5

const ResendCodeButton = ({
  onResendCode,
  endTime,
}: {
  onResendCode: () => void
  endTime: Date
}) => {
  const [counter, setCounter] = useState(45)

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date()
      const timeDiff = endTime.getTime() - now.getTime()
      const secondsLeft = Math.max(0, Math.ceil(timeDiff / 1000))
      setCounter(secondsLeft)
    }, 1000)

    return () => clearInterval(interval) // Cleans interval when component unmounts
  }, [endTime])

  const resendCodeDisabled = counter !== 0
  const buttonTitle = 'Resend code' + (resendCodeDisabled ? ` in ${counter}s` : '')

  return (
    <Button
      segmentAction="User Resent OTP Code"
      disabled={resendCodeDisabled}
      title={buttonTitle}
      onPress={onResendCode}
    />
  )
}
export default function VerifyOTP() {
  const { setEntireResponse } = useTokenStore()
  // eslint-disable-next-line @typescript-eslint/no-deprecated -- Querying using Apollo won't work in onboarding
  const { email } = useUserStore()
  const logError = useLogError()
  const { updateOptions, trackTextChange, trackFocusChange, submitData } = useSardine()

  useEffect(() => {
    void updateOptions({ flow: 'account-creation-verify-email-otp' })
  }, [updateOptions])

  const [getUserInfo] = useLazyQuery(GET_USER_INFO, {
    onCompleted(data) {
      postLogin(data.getUserInfo)
    },
  })
  const [verifyOTP, { loading: loadingOTP, error: verifyOTPError }] = useMutation(VERIFY_OTP)
  const [sendOTPToUser] = useMutation(LOGIN_USER)

  const [otpCode, setOTPCode] = useState('')
  const [endTime, setEndTime] = useState(() => new Date(Date.now() + RESEND_OTP_TIMER))

  const recaptchaEnabled = useFeatureGate(FeatureGates.RecaptchaEnabled).featureGate?.value

  async function resendCode() {
    setOTPCode('')
    setEndTime(new Date(Date.now() + RESEND_OTP_TIMER))
    if (!email) {
      logError('Email is undefined when trying to resend OTP code.')
      return
    }
    await sendOTPToUser({
      variables: {
        email,
      },
      ...(recaptchaEnabled
        ? {
            context: {
              headers: { 'X-Recaptcha-Token': await withRecaptchaVerification('SEND_OTP') },
            },
          }
        : undefined),
    })
  }

  const login = useEvent(async () => {
    try {
      void submitData()
      Keyboard.dismiss()
      if (!email) throw new Error('Email is undefined when trying to verify OTP code.')

      await verifyOTP({
        variables: { email, otpCode },
        ...(recaptchaEnabled
          ? {
              context: {
                headers: { 'X-Recaptcha-Token': await withRecaptchaVerification('VALIDATE_OTP') },
              },
            }
          : undefined),
        onCompleted(data) {
          setEntireResponse(data.validateOtpCode)
          void getUserInfo()
          void triggerSegmentAction({ action: 'Successful user log in', component: 'Button' })
        },
      })
    } catch (error) {
      logError(error)
    }
  })

  useEffect(() => {
    if (otpCode.length === OTP_LENGTH) {
      void login()
    }
  }, [otpCode, login])

  const postLogin = (userInfo: GetUserInfoQuery['getUserInfo']) => {
    if (userInfo.firstName && userInfo.lastName && userInfo.phoneNumber) {
      const userInformation = {
        ...userInfo,
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        phoneNumber: userInfo.phoneNumber,
        email: userInfo.email,
        nickname: userInfo.nickname ?? undefined,
        lastLogin: userInfo.lastLogin ?? undefined,
        createdAt: userInfo.createdAt,
        updatedAt: userInfo.updatedAt,
        picture: userInfo.picture ?? undefined,
        sourcePartnerName: userInfo.sourcePartnerName ?? undefined,
        metadata: {
          userTrackingFlag: userInfo.metadata?.userTrackingFlag ?? undefined,
        },
        cardInformation: userInfo.cardInformation
          ? {
              ...userInfo.cardInformation,
              lastFour: String(userInfo.cardInformation.lastFour),
            }
          : undefined,
      } as const satisfies userInformationInterface
      // send identify trigger to segment
      void triggerSegmentIdentify({
        userId: userInfo.id,
        userInformation,
      })
    } else {
      void triggerSegmentIdentify({ userId: userInfo.id })
    }
  }

  return (
    <OnboardingWrapper paddingHorizontal={15} testID={AuthenticationE2E.Ids.View.VerifyOTP}>
      <ScrollView>
        <BackButton href={Routes.Onboarding.route} text="Change your Email" />
        <View style={styles.textContainer}>
          <Text style={typography.XXL}>Check Those Emails</Text>
        </View>
        <View style={styles.textContainer}>
          <Text style={[styles.instructionText, typography.Base]}>
            We just sent a code to {`${email}` + '\n'}
            Didn't receive the code? Resend below.
          </Text>
        </View>
        <View style={styles.inputCodeContainer}>
          <InputCode
            numberOfDigits={OTP_LENGTH}
            segmentAction="User Inputs OTP"
            onTextChange={(text) => {
              setOTPCode(text)
              void trackTextChange('otpCode', text)
            }}
            onFocus={() => void trackFocusChange('otpCode', true)}
            onBlur={() => void trackFocusChange('otpCode', false)}
            textInputProps={{ dataSet: { [sardineIdKey]: 'otpCode' } }}
          />
        </View>
        <View style={styles.errorContainer}>
          <Text style={{ color: 'red' }}>{verifyOTPError?.message}</Text>
        </View>
      </ScrollView>
      <View style={styles.buttonContainer}>
        <Button
          disabled={otpCode.length !== OTP_LENGTH || loadingOTP}
          loading={loadingOTP}
          title="Continue"
          testID={AuthenticationE2E.Ids.Press.EmailOTPSubmit}
          onPress={() => void login()}
        />
        <ResendCodeButton onResendCode={() => void resendCode()} endTime={endTime} />
      </View>
    </OnboardingWrapper>
  )
}

const styles = StyleSheet.create({
  chevronLeft: {
    marginRight: 16,
  },
  textContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 32,
  },
  instructionText: {
    lineHeight: 24,
  },
  inputCodeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 40,
    marginBottom: Platform.OS === 'web' ? 30 : 10,
  },
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'red',
    marginBottom: 24,
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    marginBottom: 10,
  },
})
